import { useState, useRef, useEffect } from "react";
import { resendWebhooks } from "@/services/call.service";
import { useWebhookEventLogs, WebhookLogEntry, WebhookLogsResult } from "@/hooks/use-webhook-event-logs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, RefreshCw, Webhook } from "lucide-react";
import { toast } from "sonner";
import { eventsStillDue, resendToast, webhookStatus } from "./webhook-status";
import { EVENT_LABEL } from "./webhook-attempts";
import { WebhookAttempts } from "./WebhookAttempts";

const EVENTS = [
  { key: "call_started", label: "Started" },
  { key: "call_ended", label: "Ended" },
  { key: "call_analyzed", label: "Analyzed" },
];

function StatusBadge({ entry }: { entry: WebhookLogEntry }) {
  if (entry.note === "no_webhook_url") {
    return <Badge variant="outline" className="text-muted-foreground">no URL</Badge>;
  }
  const code = entry.response_code;
  if (code == null) {
    return <Badge variant="outline" className="text-muted-foreground">no response</Badge>;
  }
  const ok = code >= 200 && code < 300;
  return (
    <Badge
      variant="outline"
      className={ok ? "border-green-500/40 text-green-600" : "border-red-500/40 text-red-600"}
    >
      {code}
    </Badge>
  );
}

export function WebhookEventsTab({
  callId,
  agentId,
  open,
  onRefresh,
}: {
  callId: string;
  /** The call's agent; null once it was deleted. */
  agentId: string | null;
  open: boolean;
  onRefresh?: () => void;
}) {
  const { data, isLoading, isError, refetch } = useWebhookEventLogs(callId, open);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [showRetry, setShowRetry] = useState(false);
  const [polling, setPolling] = useState(false);

  // Background poll guard: stop polling if the dialog/tab unmounts.
  const pollRef = useRef({ cancelled: false });
  useEffect(() => {
    const guard = pollRef.current;
    guard.cancelled = false;
    return () => {
      guard.cancelled = true;
    };
  }, []);

  // After a retry, refresh the log on a WIDENING schedule so later async
  // re-deliveries (cron backoff, corrective resend) surface — and STOP EARLY
  // once every retried event shows a successful delivery. Bounded (~3 min total)
  // so a permanently-failing endpoint doesn't poll forever; the cron keeps
  // retrying server-side regardless.
  const POLL_DELAYS_MS = [2000, 4000, 7000, 12000, 18000, 27000, 40000, 60000];

  // Each event is judged by its newest attempt (the API sends them newest first)
  // or, with none, by its newest legacy entry: a call in flight at the cutover
  // can have only a legacy entry for one event and attempts for the next.
  // Retry only shows once a read succeeded, and a failed re-read keeps that
  // data, so `result` is always there.
  const latestSucceeded = (result: WebhookLogsResult, events: string[]) =>
    events.every((ev) => {
      const attempt = result.attempts?.find((a) => a.event === ev);
      if (attempt) return attempt.outcome === "2xx";
      const last = result.logs
        .filter((l) => l.event === ev)
        .sort((a, b) => (Date.parse(b.sent_at ?? "") || 0) - (Date.parse(a.sent_at ?? "") || 0))[0];
      return last?.success === true;
    });

  const pollLogs = async (events: string[]) => {
    setPolling(true);
    try {
      for (const delayMs of POLL_DELAYS_MS) {
        await new Promise((r) => setTimeout(r, delayMs));
        if (pollRef.current.cancelled) return;
        const res = await refetch();
        if (latestSucceeded(res.data, events)) return; // delivered — stop polling
      }
    } finally {
      if (!pollRef.current.cancelled) setPolling(false);
    }
  };

  const logs = (data?.logs ?? []).slice().sort((a, b) => {
    const ta = a.sent_at ? Date.parse(a.sent_at) : 0;
    const tb = b.sent_at ? Date.parse(b.sent_at) : 0;
    return tb - ta;
  });
  const attempts = data?.attempts ?? [];
  const canRetry = data?.can_retry ?? false;
  const status = webhookStatus(agentId, data?.webhook_configured ?? false);

  const toggle = (k: string) =>
    setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const allSelected = selected.length === EVENTS.length;
  const toggleAll = () => setSelected(allSelected ? [] : EVENTS.map((e) => e.key));

  // Send is disabled while nothing is selected or a send is running.
  const handleSend = async () => {
    const retriedEvents = [...selected]; // capture before clearing
    setSending(true);
    try {
      const results = await resendWebhooks(callId, retriedEvents);
      const { tone, message } = resendToast(results);
      toast[tone](message);
      setShowRetry(false);
      setSelected([]);
      await refetch();
      onRefresh?.();
      // Keep refreshing the log on a widening schedule until delivered. An event
      // given up on, held or with no URL gets no attempt to wait for: the
      // toast has said so, and the read above is the last one it needs.
      const due = eventsStillDue(retriedEvents, results);
      if (due.length > 0) void pollLogs(due);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Retry failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* Retry controls */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {status.heading}
          {polling && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </div>
        {canRetry && (
          showRetry ? (
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} /> All
              </label>
              {EVENTS.map((e) => (
                <label key={e.key} className="flex items-center gap-1.5 text-xs">
                  <Checkbox checked={selected.includes(e.key)} onCheckedChange={() => toggle(e.key)} />
                  {e.label}
                </label>
              ))}
              <Button size="sm" onClick={handleSend} disabled={selected.length === 0 || sending}>
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Send
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowRetry(false); setSelected([]); }} disabled={sending}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowRetry(true)}
              disabled={!!status.retryOffBecause}
              title={status.retryOffBecause}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
            </Button>
          )
        )}
      </div>

      {/* Log list: the call's attempts; the legacy log for a call from before them */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading webhook logs…
        </div>
      ) : isError && !data ? (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 text-muted-foreground">
          <Webhook className="w-4 h-4 opacity-50" />
          <span className="text-sm">Couldn't load the webhook log. Close the call and open it again to retry.</span>
        </div>
      ) : attempts.length > 0 ? (
        <WebhookAttempts attempts={attempts} />
      ) : logs.length === 0 ? (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 text-muted-foreground">
          <Webhook className="w-4 h-4 opacity-50" />
          <span className="text-sm">No webhook attempts recorded yet.</span>
        </div>
      ) : (
        <Accordion type="multiple" className="space-y-2">
          {logs.map((entry, i) => (
            <AccordionItem key={i} value={`log-${i}`} className="border rounded-lg px-3">
              <AccordionTrigger className="hover:no-underline py-2.5">
                <div className="flex flex-1 items-center gap-3 text-left">
                  <Badge variant="secondary" className="capitalize">
                    {EVENT_LABEL[entry.event] ?? entry.event}
                  </Badge>
                  <StatusBadge entry={entry} />
                  {entry.source && (
                    <span className="text-xs text-muted-foreground">{entry.source}</span>
                  )}
                  <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">
                    {entry.sent_at ? new Date(entry.sent_at).toLocaleString() : "—"}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pb-2 text-xs">
                  {"url" in entry && (
                    <div>
                      <span className="text-muted-foreground">URL: </span>
                      <span className="break-all">{entry.url ?? "—"}</span>
                    </div>
                  )}
                  {entry.payload_summary != null && (
                    <div>
                      <div className="text-muted-foreground mb-1">Payload summary</div>
                      <pre className="bg-muted/40 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                        {JSON.stringify(entry.payload_summary, null, 2)}
                      </pre>
                    </div>
                  )}
                  {(entry.response_body ?? entry.error) && (
                    <div>
                      <div className="text-muted-foreground mb-1">
                        {entry.success ? "Response" : "Response / error"}
                      </div>
                      <pre className="bg-muted/40 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
                        {entry.response_body ?? entry.error}
                      </pre>
                    </div>
                  )}
                  {!("url" in entry) && entry.payload_summary == null && !(entry.response_body ?? entry.error) && (
                    <div className="text-muted-foreground italic">
                      Metadata only — full payload requires developer access.
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
